import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Description,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import LinkPreview, { getLinkPreviewData } from "../LinkPreview";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: "#0035a2",
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 420,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 420,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  messagePending: {
    opacity: 0.78,
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  outgoingAuthor: {
    display: "flex",
    flexDirection: "column",
    padding: "2px 80px 0 6px",
    lineHeight: 1.2,
  },
  outgoingAuthorName: {
    color: "#2f7d32",
    fontSize: 12,
    fontWeight: 600,
  },
  outgoingAuthorQueue: {
    color: "#6b6b6b",
    fontSize: 11,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    minHeight: 22,
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "100%",
    maxWidth: 340,
    maxHeight: 420,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    display: "block",
  },
  mediaPreview: {
    marginTop: 2,
    marginBottom: 2,
    borderRadius: 8,
    overflow: "hidden",
    maxWidth: 340,
    backgroundColor: "#00000008",
  },
  documentPreview: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    backgroundColor: "#fff",
  },
  documentInfo: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  documentName: {
    fontSize: 14,
    fontWeight: 500,
    overflowWrap: "anywhere",
  },
  documentType: {
    fontSize: 12,
    color: "#666",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  internalNote: {
    alignSelf: "center",
    maxWidth: 520,
    margin: "10px 0",
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "#fff4c2",
    color: "#4f4630",
    fontSize: 12,
    lineHeight: 1.45,
    boxShadow: "0 1px 1px #b3b3b3",
    whiteSpace: "pre-wrap",
  },

  internalNoteTime: {
    display: "block",
    marginTop: 4,
    color: "#8a7f5a",
    fontSize: 10,
    textAlign: "right",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  ackStatusText: {
    marginLeft: 4,
    fontSize: 10,
    fontWeight: 600,
    verticalAlign: "middle",
  },
  ackFailedText: {
    color: theme.palette.error.main,
  },
  ackPendingText: {
    color: "#8a6d1d",
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
    // ===== NOVOS ESTILOS =====
  sticker: {
    width: 160,
    height: 160,
    objectFit: "contain",
    cursor: "pointer",
    backgroundColor: "transparent",
    display: "block",
  },
  stickerMessageLeft: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,
    minWidth: "auto",
  },
  stickerMessageRight: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,
    minWidth: "auto",
  },

  stickerModalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },

  stickerModalImage: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    objectFit: "contain",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "DELETE_MESSAGE") {
    return state.filter((message) => message.id !== action.payload);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup, pendingMessages = [] }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  const [stickerModalOpen, setStickerModalOpen] = useState(false);
  const [stickerUrl, setStickerUrl] = useState(null);
  const normalizeComparableBody = (value) =>
    String(value || "").replace(/^\u200e+/, "");

  const isSameOutgoingMessage = (pendingMessage, sentMessage) => {
    if (!pendingMessage?.fromMe || !sentMessage?.fromMe) return false;

    const sameQuotedMessage =
      (pendingMessage.quotedMsg?.id || null) ===
      (sentMessage.quotedMsg?.id || null);

    if (pendingMessage.mediaType || sentMessage.mediaType) {
      return (
        sameQuotedMessage &&
        pendingMessage.mediaType &&
        pendingMessage.mediaType === sentMessage.mediaType
      );
    }

    return (
      sameQuotedMessage &&
      normalizeComparableBody(pendingMessage.body) ===
        normalizeComparableBody(sentMessage.body)
    );
  };

  const visiblePendingMessages = pendingMessages.filter(
    (pendingMessage) =>
      !messagesList.some((message) =>
        isSameOutgoingMessage(pendingMessage, message)
      )
  );

  const displayedMessages = [...messagesList, ...visiblePendingMessages].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const handleOpenSticker = (url) => {
    setStickerUrl(url);
    setStickerModalOpen(true);
  };

  const handleCloseSticker = () => {
    setStickerModalOpen(false);
    setStickerUrl(null);
  };


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(() => {
    if (pendingMessages.length > 0) {
      scrollToBottom();
    }
  }, [pendingMessages.length]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const isStickerMessage = (message) => /\.webp(\?|$)/i.test(message.mediaUrl || "");

  const parseLegacyOutgoingBody = (body) => {
    const text = String(body || "");
    const match = text.match(/^\*{1,2}([^*\n:]+(?:\s*\([^)\n]+\))?)\s*:\*{1,2}\s*\n([\s\S]*)$/);

    if (!match) {
      return { author: "", cleanBody: text };
    }

    return {
      author: match[1].trim(),
      cleanBody: match[2]
    };
  };

  const getDisplayBody = (message) => {
    if (!message.fromMe) return message.body;
    return parseLegacyOutgoingBody(message.body).cleanBody;
  };

  const getOutgoingAuthor = (message) => {
    if (!message.fromMe) return null;

    const legacyAuthor = parseLegacyOutgoingBody(message.body).author;
    const name = message.user?.name || legacyAuthor;
    const queue = message.queue?.name;

    if (!name && !queue) return null;

    return { name, queue };
  };

  const shouldRenderMessageBody = (message) => {
    if (message.isDeleted) return true;
    if (!message.mediaUrl) return true;
    if (message.mediaType === "location" || message.mediaType === "vcard") {
      return true;
    }
    return false;
  };

  const renderLinkPreview = (message) => {
    if (message.mediaUrl || message.isDeleted) return null;

    const preview = getLinkPreviewData(getDisplayBody(message));
    if (!preview) return null;

    return (
      <LinkPreview
        url={preview.url}
        title={preview.title}
        subtitle={preview.subtitle}
      />
    );
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    } else
      if (/\.webp$/i.test(message.mediaUrl)) {
        return (
          <img
            src={message.mediaUrl}
            alt="sticker"
            className={classes.sticker}
            onClick={() => handleOpenSticker(message.mediaUrl)}
            draggable={false}
          />
        );
      }
      else if (message.mediaType === "vcard") {
        let array = message.body.split("\n");
        let obj = [];
        let contact = "";
        for (let index = 0; index < array.length; index++) {
          const v = array[index];
          let values = v.split(":");
          for (let ind = 0; ind < values.length; ind++) {
            if (values[ind].indexOf("+") !== -1) {
              obj.push({ number: values[ind] });
            }
            if (values[ind].indexOf("FN") !== -1) {
              contact = values[ind + 1];
            }
          }
        }
        return <VcardPreview contact={contact} numbers={obj[0]?.number} />
      }
      else if (/^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
        return (
          <div className={classes.mediaPreview}>
            <ModalImageCors imageUrl={message.mediaUrl} />
          </div>
        );
      } else if (message.mediaType === "audio") {
        return <Audio url={message.mediaUrl} />
      } else if (message.mediaType === "video") {
        return (
          <div className={classes.mediaPreview}>
            <video
              className={classes.messageMedia}
              src={message.mediaUrl}
              controls
            />
          </div>
        );
      } else {
        return (
          <>
            <div className={classes.mediaPreview}>
              <div className={classes.documentPreview}>
                <Description color="action" />
                <div className={classes.documentInfo}>
                  <span className={classes.documentName}>
                    {message.body || "Arquivo"}
                  </span>
                  <span className={classes.documentType}>Anexo</span>
                </div>
              </div>
            </div>

            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                color="primary"
                variant="outlined"
                target="_blank"
                href={message.mediaUrl}
              >
                BAIXAR MÍDIA
              </Button>
            </div>
            <Divider />
          </>
        );
      }
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return (
        <Tooltip title="Falhou. Verifique a instância ou tente reenviar.">
          <span>
            <Block color="error" fontSize="small" className={classes.ackIcons} />
            <span className={clsx(classes.ackStatusText, classes.ackFailedText)}>
              Falhou
            </span>
          </span>
        </Tooltip>
      );
    }
    if (message.ack === 0) {
      return (
        <Tooltip title="Enviando ou aguardando confirmação.">
          <span>
            <AccessTime fontSize="small" className={classes.ackIcons} />
            <span className={clsx(classes.ackStatusText, classes.ackPendingText)}>
              Enviando
            </span>
          </span>
        </Tooltip>
      );
    }
    if (message.ack === 1) {
      return (
        <Tooltip title="Enviado para o WhatsApp.">
          <Done fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 2) {
      return (
        <Tooltip title="Entregue ao destinatário.">
          <DoneAll fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <Tooltip title="Lido pelo destinatário.">
          <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
        </Tooltip>
      );
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(displayedMessages[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < displayedMessages.length - 1) {
      let messageDay = parseISO(displayedMessages[index].createdAt);
      let previousMessageDay = parseISO(displayedMessages[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(displayedMessages[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === displayedMessages.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < displayedMessages.length && index > 0) {
      let messageUser = displayedMessages[index].fromMe;
      let previousMessageUser = displayedMessages[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {getDisplayBody(message.quotedMsg)}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (displayedMessages.length > 0) {
      const viewMessagesList = displayedMessages.map((message, index) => {
        if (message.isInternal) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className={classes.internalNote}>
                <MarkdownWrapper>{message.body}</MarkdownWrapper>
                <span className={classes.internalNoteTime}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
                {stickerModalOpen && (
                  <div
                    className={classes.stickerModalOverlay}
                    onClick={handleCloseSticker}
                  >
                    <img
                      src={stickerUrl}
                      alt="sticker-modal"
                      className={classes.stickerModalImage}
                    />
                  </div>
                )}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.stickerMessageLeft]: isStickerMessage(message),
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || message.isPending}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {shouldRenderMessageBody(message) && (
                    <MarkdownWrapper>{getDisplayBody(message)}</MarkdownWrapper>
                  )}
                  {renderLinkPreview(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const outgoingAuthor = getOutgoingAuthor(message);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.stickerMessageRight]: isStickerMessage(message),
                  [classes.messagePending]: message.isPending,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || message.isPending}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                {outgoingAuthor && !isStickerMessage(message) && (
                  <div className={classes.outgoingAuthor}>
                    {outgoingAuthor.name && (
                      <span className={classes.outgoingAuthorName}>
                        {outgoingAuthor.name}
                      </span>
                    )}
                    {outgoingAuthor.queue && (
                      <span className={classes.outgoingAuthorQueue}>
                        {outgoingAuthor.queue}
                      </span>
                    )}
                  </div>
                )}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {shouldRenderMessageBody(message) && (
                    <MarkdownWrapper>{getDisplayBody(message)}</MarkdownWrapper>
                  )}
                  {renderLinkPreview(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
        <div
          id="messagesList"
          className={classes.messagesList}
          onScroll={handleScroll}
        >
        {displayedMessages.length > 0 ? renderMessages() : []}
        </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
